// src/pages/HelpCenter.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./HelpCenter.css";

export default function HelpCenter() {
  const [activeCategory, setActiveCategory] = useState("getting-started");
  const [searchTerm, setSearchTerm] = useState("");

  const categories = [
    { id: "getting-started", name: "Getting Started", icon: "🚀" },
    { id: "account", name: "Account & Profile", icon: "👤" },
    { id: "connections", name: "Connections & Requests", icon: "🤝" },
    { id: "trips", name: "Trip Planning", icon: "✈️" },
    { id: "agencies", name: "Working with Agencies", icon: "🏢" },
    { id: "safety", name: "Safety & Security", icon: "🛡️" },
    { id: "ratings", name: "Ratings & Reviews", icon: "⭐" },
    { id: "technical", name: "Technical Issues", icon: "🔧" }
  ];

  const faqs = {
    "getting-started": [
      {
        question: "What is Go With Me Buddy?",
        answer: "Go With Me Buddy is a platform that connects travelers with like-minded travel partners and verified travel agencies. Whether you're planning a solo adventure or looking for group travel options, we help you find the perfect companions and services for your journey."
      },
      {
        question: "How do I create an account?",
        answer: "Click on 'Register' in the top navigation, choose your role (Partner or Agency), fill in your details including name, email, and password, and submit the form. You'll receive instant access to start connecting with travelers or partners."
      },
      {
        question: "Is the platform free to use?",
        answer: "Yes! Creating an account, browsing partners/agencies, and making connections is completely free. Our mission is to make travel accessible and social for everyone."
      },
      {
        question: "What's the difference between Partner and Agency accounts?",
        answer: "Partner accounts are for individual travelers looking for travel buddies. Agency accounts are for travel businesses offering services like tour packages, bookings, and travel planning. Choose based on your needs!"
      }
    ],
    "account": [
      {
        question: "How do I update my profile?",
        answer: "Click on your profile icon in the navigation bar, select 'Profile', and you'll be able to edit your personal information, add a photo, update your bio, and manage your travel preferences."
      },
      {
        question: "Can I change my role from Partner to Agency?",
        answer: "Currently, role changes require creating a new account. Please contact our support team at gowithmebuddy1@gmail.com if you need assistance with this transition."
      },
      {
        question: "How do I reset my password?",
        answer: "On the login page, click 'Forgot Password', enter your registered email address, and you'll receive password reset instructions. Follow the link in the email to create a new password."
      },
      {
        question: "Can I delete my account?",
        answer: "Yes, you can request account deletion by contacting our support team at gowithmebuddy1@gmail.com. Please note that this action is permanent and cannot be undone."
      }
    ],
    "connections": [
      {
        question: "How do I send a connection request?",
        answer: "Browse the 'Find Partner' page, find someone whose trip interests align with yours, and click 'Send Request'. Write a personalized message explaining why you'd like to connect, and submit!"
      },
      {
        question: "What happens after I send a request?",
        answer: "The recipient will receive a notification and can view your profile and trip details. They can choose to accept or reject your request. You'll be notified of their decision."
      },
      {
        question: "How do I accept or reject requests?",
        answer: "Go to the 'Received Requests' tab on the Find Partner or Agencies page. Review each request, check the sender's profile and message, then click 'Accept' or 'Reject' based on your preference."
      },
      {
        question: "Can I cancel a sent request?",
        answer: "Currently, sent requests cannot be canceled. However, you can always discuss with the recipient if you change your plans."
      },
      {
        question: "How do I view my connected partners?",
        answer: "After accepting a request, you can view contact details by clicking 'View Contact' on the accepted request card. This shows email and phone information for direct communication."
      }
    ],
    "trips": [
      {
        question: "How do I create a trip?",
        answer: "Go to 'My Trips' in the navigation menu, click 'Create New Trip', fill in details like destination, dates, budget, duration, and interests. Your trips will be visible to other users!"
      },
      {
        question: "Can I edit my trip details?",
        answer: "Yes! Go to 'My Trips', find the trip you want to modify, and click the edit icon. Update any information and save your changes."
      },
      {
        question: "How many trips can I create?",
        answer: "There's no limit! Create as many trips as you're planning. This helps you connect with different partners for different adventures."
      },
      {
        question: "Can I delete a trip?",
        answer: "Yes, you can delete trips from the 'My Trips' page. Click the delete icon on the trip card you want to remove."
      }
    ],
    "agencies": [
      {
        question: "How do agencies work on this platform?",
        answer: "Travel agencies can create profiles showcasing their services, packages, and specializations. Partners can browse agencies, send queries, and receive customized travel planning assistance."
      },
      {
        question: "How do I contact an agency?",
        answer: "Visit the 'Agencies' page, browse available agencies, and click 'Send Query' on the agency you're interested in. Write your travel requirements, and the agency will respond with options."
      },
      {
        question: "Are agencies verified?",
        answer: "We display ratings and reviews for all agencies. Check the average rating and read reviews from other travelers before making your decision."
      },
      {
        question: "What if an agency doesn't respond?",
        answer: "Most agencies respond within 24-48 hours. If you don't hear back, you can send a query to another agency or contact us for assistance."
      }
    ],
    "safety": [
      {
        question: "How do I stay safe while traveling with partners?",
        answer: "Always meet in public places first, share your itinerary with family/friends, trust your instincts, and use our rating system to check partner reviews. See our detailed Safety Tips page for more information."
      },
      {
        question: "How can I report suspicious behavior?",
        answer: "If you encounter any suspicious activity or inappropriate behavior, contact us immediately at gowithmebuddy1@gmail.com with details and screenshots if possible."
      },
      {
        question: "Is my personal information protected?",
        answer: "Yes! Your contact details are only shared with partners after you accept their connection request. We never share your information with third parties without consent."
      },
      {
        question: "What should I verify before accepting a request?",
        answer: "Check the user's profile completeness, read their bio, review their trips, check their ratings if available, and ensure their travel style matches yours."
      }
    ],
    "ratings": [
      {
        question: "How do I rate a partner or agency?",
        answer: "After completing a trip or service, click the 'Rate' button on their profile or request card. Choose 1-5 stars and write optional feedback about your experience."
      },
      {
        question: "Can I see who rated me?",
        answer: "Yes, you can view all ratings received in your profile section. Each rating shows the rater's name, rating score, feedback, and date."
      },
      {
        question: "Can I edit or delete my rating?",
        answer: "Currently, ratings cannot be edited or deleted to maintain authenticity. Please rate carefully and thoughtfully."
      },
      {
        question: "How are average ratings calculated?",
        answer: "Average ratings are calculated by summing all ratings received and dividing by the total number of ratings. This is displayed on profiles and listings."
      }
    ],
    "technical": [
      {
        question: "The website is not loading properly",
        answer: "Try clearing your browser cache, refreshing the page, or using a different browser. If the issue persists, contact our support team with details about your browser and device."
      },
      {
        question: "I'm not receiving email notifications",
        answer: "Check your spam/junk folder first. Add gowithmebuddy1@gmail.com to your contacts. If still not receiving emails, contact support."
      },
      {
        question: "Images are not uploading",
        answer: "Ensure your image is under 5MB in size and in JPG, PNG, or JPEG format. Try compressing the image or using a different file."
      },
      {
        question: "I found a bug, how do I report it?",
        answer: "We appreciate bug reports! Email us at gowithmebuddy1@gmail.com with a description of the issue, screenshots, and steps to reproduce the problem."
      }
    ]
  };

  const filteredFaqs = faqs[activeCategory].filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="help-center-container">
      {/* Header */}
      <div className="help-header">
        <div className="help-header-content">
          <h1 className="help-title">❓ Help Center</h1>
          <p className="help-subtitle">Find answers to your questions and get help</p>
          
          {/* Search Bar */}
          <div className="help-search">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search for help..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="help-search-input"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="help-content">
        <div className="help-container">
          {/* Categories Sidebar */}
          <div className="help-sidebar">
            <h3 className="sidebar-title">Categories</h3>
            <div className="category-list">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setSearchTerm("");
                  }}
                >
                  <span className="category-icon">{category.icon}</span>
                  <span className="category-name">{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="help-main">
            <div className="faq-header">
              <h2 className="faq-title">
                {categories.find(c => c.id === activeCategory)?.icon}{' '}
                {categories.find(c => c.id === activeCategory)?.name}
              </h2>
              <p className="faq-count">{filteredFaqs.length} articles</p>
            </div>

            <div className="faq-list">
              {filteredFaqs.length > 0 ? (
                filteredFaqs.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <h3 className="faq-question">
                      <span className="faq-icon">Q.</span>
                      {faq.question}
                    </h3>
                    <p className="faq-answer">
                      <span className="answer-icon">A.</span>
                      {faq.answer}
                    </p>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <p className="no-results-icon">🔍</p>
                  <h3>No articles found</h3>
                  <p>Try adjusting your search or browse other categories</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Support Section */}
      <div className="contact-support-section">
        <div className="contact-support-content">
          <h2 className="contact-title">Still need help?</h2>
          <p className="contact-description">
            Can't find what you're looking for? Our support team is here to help!
          </p>
          <div className="contact-actions">
            <a href="mailto:gowithmebuddy1@gmail.com" className="contact-btn primary">
              📧 Email Support
            </a>
            <Link to="/safety" className="contact-btn secondary">
              🛡️ Safety Tips
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
